import React from 'react';

import {
  CategoryScale,
  Chart as ChartJS,
  LineElement,
  LinearScale,
  PointElement,
  TimeScale,
  Tooltip,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Line } from 'react-chartjs-2';

import 'chartjs-adapter-moment';
import { useReportData } from '../../report/reportContext';

const LINEARIZATION_FACTOR = 10;

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  TimeScale,
  annotationPlugin
);

function areSameDay(date1, date2) {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

const calculateAge = (date, birthdateString) => {
  const birthdate = new Date(birthdateString);
  const ageInMS = date - birthdate;
  return Math.ceil(ageInMS / (1000 * 60 * 60 * 365 * 24));
};

export const PSADiagram = ({ reference, setFinishedRendering }) => {
  const {
    reportData: { report, study },
  } = useReportData();

  const dates = report.psaValues.psaSeries.map(series => {
    const date = new Date(series.testDate);
    date.setDate(1);
    return date;
  });

  const timeHorizonInDays = Math.ceil(
    (dates[dates.length - 1] - dates[0]) / (1000 * 60 * 60 * 24)
  );
  let lastPrintedTick = dates[0];
  const printedValues = [dates[0], dates[dates.length - 1]];

  const psaValues = report.psaValues.psaSeries.map(series => series.psaValue);

  const options = {
    devicePixelRatio: 2.5,
    animation: {
      onComplete: chart => {
        setFinishedRendering(true);
      },
      duration: 1,
    },
    plugins: {
      annotation: {
        annotations: {
          line1: {
            type: 'line',
            yMin: 4,
            yMax: 4,
            borderColor: 'red',
            borderWidth: 1,
          },
        },
      },
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'month',
          displayFormats: {
            month: 'MM/YY',
          },
          tooltipFormat: 'MM/YY',
        },
        ticks: {
          source: 'labels',
          color: '#C4C4C4',
          font: {
            size: 11,
          },
          callback: (value, index) => {
            const date = dates[index];

            if (
              areSameDay(dates[0], date) ||
              areSameDay(dates[dates.length - 1], date)
            ) {
              return [value, `${calculateAge(date, study.birthDate)} Jahre`];
            }

            const daysSinceLastPrintedTick = Math.ceil(
              (date - lastPrintedTick) / (1000 * 60 * 60 * 24)
            );
            const daysTillLastMeasuredValues = Math.ceil(
              (dates[dates.length - 1] - date) / (1000 * 60 * 60 * 24)
            );

            if (
              daysSinceLastPrintedTick >=
                timeHorizonInDays / LINEARIZATION_FACTOR &&
              !printedValues.includes(date) &&
              daysTillLastMeasuredValues >=
                timeHorizonInDays / LINEARIZATION_FACTOR
            ) {
              printedValues.push(date);
              lastPrintedTick = date;
              return [value, `${calculateAge(date, study.birthDate)} Jahre`];
            }

            return '';
          },
        },
        grid: {
          color: context => {
            if (context.index === 0) return 'rgba(255, 255, 255, 0.2)';
          },
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: '#C4C4C4',
        },
        grid: {
          width: 0.5,
          source: 'data',
          color: 'rgba(255, 255, 255, 0.2)',
        },
        border: {
          display: false,
        },
      },
    },
  };

  const data = {
    labels: dates,
    datasets: [
      {
        borderColor: '#C4C4C4',
        width: 0.5,
        pointRadius: 4,
        pointOpacity: 1,
        pointBackgroundColor: '#C4C4C4',
        data: psaValues,
      },
    ],
  };

  return <Line ref={reference} options={options} data={data} />;
};
